import { useState, FormEvent } from 'react';

export default function LeadForm() {
  const [formData, setFormData] = useState({
    name: '',
    businessName: '',
    email: '',
    phone: '',
    websiteUrl: '',
    businessType: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('businessName', formData.businessName);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('websiteUrl', formData.websiteUrl);
      formBody.append('businessType', formData.businessType);

      const response = await fetch('https://readdy.ai/api/form/d6a6ul5bd17lru29lki0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          businessName: '',
          email: '',
          phone: '',
          websiteUrl: '',
          businessType: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="audit-form" className="py-24 px-6 bg-[#0A0A0A] relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#3B82F6]/5 to-transparent"></div>
      
      <div className="max-w-3xl mx-auto relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-black mb-4">
            Get Your <span className="text-[#3B82F6]">Free Website Mockup.</span>
          </h2>
          <p className="text-gray-400 text-lg">
            We'll build you a simple one page site mock up for free.
          </p>
        </div>

        <form 
          id="website-audit-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="bg-[#151515] border-2 border-[#3B82F6]/20 rounded-2xl p-8 md:p-12 space-y-6"
        >
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-bold mb-2 text-gray-300">
                Your Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full bg-[#0A0A0A] border border-[#3B82F6]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#3B82F6] transition-all"
                placeholder="John Smith"
              />
            </div>

            <div>
              <label htmlFor="businessName" className="block text-sm font-bold mb-2 text-gray-300">
                Business Name *
              </label>
              <input
                type="text"
                id="businessName"
                name="businessName"
                required
                value={formData.businessName}
                onChange={(e) => setFormData({...formData, businessName: e.target.value})}
                className="w-full bg-[#0A0A0A] border border-[#3B82F6]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#3B82F6] transition-all"
                placeholder="Smith Plumbing"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="email" className="block text-sm font-bold mb-2 text-gray-300">
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full bg-[#0A0A0A] border border-[#3B82F6]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#3B82F6] transition-all"
                placeholder="john@smithplumbing.com"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-bold mb-2 text-gray-300">
                Phone *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="w-full bg-[#0A0A0A] border border-[#3B82F6]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#3B82F6] transition-all"
                placeholder="(555) 123-4567"
              />
            </div>
          </div>

          <div>
            <label htmlFor="websiteUrl" className="block text-sm font-bold mb-2 text-gray-300">
              Current Website URL
            </label>
            <input
              type="text"
              id="websiteUrl"
              name="websiteUrl"
              value={formData.websiteUrl}
              onChange={(e) => setFormData({...formData, websiteUrl: e.target.value})}
              className="w-full bg-[#0A0A0A] border border-[#3B82F6]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#3B82F6] transition-all"
              placeholder="www.yourwebsite.com"
            />
          </div>

          <div>
            <label htmlFor="businessType" className="block text-sm font-bold mb-2 text-gray-300">
              What type of service business are you? *
            </label>
            <select
              id="businessType"
              name="businessType"
              required
              value={formData.businessType}
              onChange={(e) => setFormData({...formData, businessType: e.target.value})}
              className="w-full bg-[#0A0A0A] border border-[#3B82F6]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#3B82F6] transition-all cursor-pointer"
            >
              <option value="">Select your business type</option>
              <option value="Plumbing">Plumbing</option>
              <option value="Electrical">Electrical</option>
              <option value="HVAC">HVAC</option>
              <option value="Landscaping">Landscaping</option>
              <option value="Salon/Spa">Salon/Spa</option>
              <option value="Cleaning">Cleaning</option>
              <option value="Roofing">Roofing</option>
              <option value="Painting">Painting</option>
              <option value="Pest Control">Pest Control</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#3B82F6] text-white py-5 rounded-lg text-lg font-black hover:bg-[#2563EB] transition-all shadow-lg shadow-[#3B82F6]/20 hover:shadow-[#3B82F6]/40 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {isSubmitting ? 'Submitting...' : 'Show Me My Free Mockup'}
          </button>

          {submitStatus === 'success' && (
            <div className="bg-[#3B82F6]/10 border border-[#3B82F6] rounded-lg p-4 text-center">
              <p className="text-[#3B82F6] font-bold">
                <i className="ri-check-line mr-2"></i>
                Thanks! We'll send your mockup within 24 hours.
              </p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="bg-red-500/10 border border-red-500 rounded-lg p-4 text-center">
              <p className="text-red-400 font-bold">
                <i className="ri-error-warning-line mr-2"></i>
                Something went wrong. Please try again.
              </p>
            </div>
          )}

          <p className="text-center text-gray-500 text-sm">
            No pressure. We'll show you what a modern site could do for your business.
          </p>
        </form>

        {/* Final CTA */}
        <div className="mt-20 text-center bg-gradient-to-r from-[#3B82F6]/10 via-[#3B82F6]/5 to-[#3B82F6]/10 border-2 border-[#3B82F6]/30 rounded-2xl p-6 sm:p-12">
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-black mb-6">
            Stop Losing Jobs to a <span className="text-[#3B82F6]">Bad Website.</span>
          </h3>
          <button
            onClick={() => {
              const form = document.getElementById('website-audit-form');
              if (form) {
                form.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }}
            className="bg-[#3B82F6] text-white px-6 sm:px-10 py-4 sm:py-5 rounded-lg text-lg sm:text-xl font-black hover:bg-[#2563EB] transition-all shadow-lg shadow-[#3B82F6]/20 hover:shadow-[#3B82F6]/40 whitespace-nowrap cursor-pointer"
          >
            Request My Free Mockup
          </button>
          <p className="mt-6 text-gray-400 font-medium text-sm sm:text-base">
            Built specifically for local service businesses.
          </p>
        </div>
      </div>
    </section>
  );
}
